/**
 * GDPR consent client — bridges the storefront cookie banner to the
 * AgenticEcom_GdprPro Magento module so consent is (a) rendered from the
 * merchant's configured categories/policy URLs and (b) logged server-side for
 * the compliance audit trail (not just stashed in localStorage).
 *
 * Soft-fails everywhere: if the module is disabled or unreachable the banner
 * falls back to its built-in local-only behaviour, so the storefront never
 * breaks because of GDPR config.
 */

import { gql } from '@agentic/magento-client/lib/graphql-mini';
import { graphqlRequest } from './graphql-client';

export interface ConsentCategory {
    code: string;
    label: string;
    description?: string | null;
    is_required?: boolean | null;
    default_enabled?: boolean | null;
}

export interface GdprConsentConfig {
    enabled: boolean;
    cookie_policy_url?: string | null;
    privacy_policy_url?: string | null;
    default_consent?: string | null; // 'granted' | 'denied'
    consent_categories?: ConsentCategory[] | null;
}

const GET_CONFIG = gql`
  query GdprConsentConfig {
    gdprConsentConfig {
      enabled
      cookie_policy_url
      privacy_policy_url
      default_consent
      consent_categories { code label description is_required default_enabled }
    }
  }
`;

const SAVE_CONSENT = gql`
  mutation SaveCookieConsent($necessary: Boolean!, $analytics: Boolean, $marketing: Boolean, $preferences: Boolean) {
    saveCookieConsent(necessary: $necessary, analytics: $analytics, marketing: $marketing, preferences: $preferences) {
      success
      message
    }
  }
`;

/** Fetch the merchant's GDPR consent config. Returns null on failure. */
export async function getGdprConsentConfig(): Promise<GdprConsentConfig | null> {
    try {
        const d = await graphqlRequest<{ gdprConsentConfig: GdprConsentConfig | null }>(
            GET_CONFIG, undefined, { silent: true },
        );
        return d?.gdprConsentConfig ?? null;
    } catch {
        return null;
    }
}

/**
 * Persist the visitor's consent choice to Magento for the audit log. Best-effort
 * — the banner has already stored the choice locally + applied it, so a failed
 * server log must not block the UX. Returns true on success.
 */
export async function saveCookieConsent(prefs: {
    necessary: boolean;
    analytics?: boolean;
    marketing?: boolean;
    preferences?: boolean;
}): Promise<boolean> {
    try {
        const d = await graphqlRequest<{ saveCookieConsent: { success: boolean } }>(
            SAVE_CONSENT,
            {
                necessary: prefs.necessary,
                analytics: !!prefs.analytics,
                marketing: !!prefs.marketing,
                preferences: !!prefs.preferences,
            },
            { silent: true },
        );
        return !!d?.saveCookieConsent?.success;
    } catch {
        return false;
    }
}
